import { format } from 'date-fns';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClassTypeLabel } from '../classes/ClassTypeLabel';
import type { UserClassInterface } from '../../types/userClass.interface';
import type { ClassInterface } from '../../types/class.interface';
import type { VideoInterface } from '../../types/video.interface';
import { CalendarDays, CheckCircle, Clock, Plus, Star } from 'lucide-react';
import { CancelClassModal, SuccessModal, InviteAFriendModal, ErrorModal, StudioReviewModal } from '@beholos/holoscommon';
import { cancelUserClass } from '../../services/class.service';
import { inviteFriendToClass } from '../../services/schedule.service';
import LiveStreamJoinButton from './LiveStreamJoinButton';

export const UserClassRow = ({ data }: { data: UserClassInterface }) => {
  const navigate = useNavigate();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isOpenReviewModal, setIsOpenReviewModal] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isInviteSuccessModalOpen, setIsInviteSuccessModalOpen] = useState(false);
  const [isInviteErrorModalOpen, setIsInviteErrorModalOpen] = useState(false);
  const [inviteErrorMessage, setInviteErrorMessage] = useState('');
  const [cancelErrorMessage, setCancelErrorMessage] = useState('');
  const [isCancelErrorModalOpen, setIsCancelErrorModalOpen] = useState(false);

  let _class: ClassInterface | VideoInterface | undefined = data.class;
  const isVideo = !!data.video || !!data.userVideo;
  if (isVideo) {
    _class = data.video || (data.userVideo || {}).video;
  }
  const start = new Date(data.startTime);
  const end = new Date(data.endTime);
  const dateStr = format(start, 'dd/MM/yyyy');
  const timeStr = format(start, 'HH:mm');
  const durationMin = Math.round((end.getTime() - start.getTime()) / 1000 / 60);
  const passes = data.class?.passes || 0;
  const code = data.userClassHash?.classHash.toUpperCase();
  const attended = data.attended;
  const cancelled = data.cancelled;
  const canBeReviewed = !isVideo && !data.reviewGiven && data.attended && !data.cancelled;

  // Determine if this is an upcoming class that can be cancelled
  const isUpcoming = new Date(data.startTime) > new Date() && !cancelled && !attended;
  const partnerType = _class?.studio?.partnerType || '';
  const hasNotEnded = new Date(data.endTime) > new Date() && !cancelled;

  // Prepare cancel class data for modal
  const getCancelClassData = () => ({
    classId: data._id,
    passes: passes,
    classStartTime: data.startTime,
    classEndTime: data.endTime,
    cancelTimeRestriction: data.class?.cancelTimeRestriction || 0,
    isVideo: isVideo,
    studioName: data.class?.studio?.name || '',
    className: _class?.title || ''
  });

  // Handle cancel class confirmation
  const handleCancelClass = async (classId: string, _passes: number) => {
    try {
      const { status, message } = await cancelUserClass(classId);
      setIsCancelModalOpen(false);
      if (status !== 200) {
        throw new Error(message || 'Error al cancelar la clase');
      }

      setIsSuccessModalOpen(true);
    } catch (error) {
      console.error('Error cancelling class:', error);
      setIsCancelErrorModalOpen(true);
      setCancelErrorMessage(error instanceof Error ? error.message : 'Error de conexión. Por favor intenta de nuevo.');
    }
  };

  // Handle success modal close
  const handleSuccessModalClose = () => {
    setIsSuccessModalOpen(false);
    // Refresh the page to update the class list
    window.location.reload();
  };

  // Handle invite friend
  const handleInviteFriend = async (email: string, userClassId: string) => {
    try {
      const result = await inviteFriendToClass(email, userClassId);

      if (result.success) {
        // Close invite modal and show success modal
        setIsInviteModalOpen(false);
        setIsInviteSuccessModalOpen(true);
      } else {
        // Show error modal
        setInviteErrorMessage(result.error || 'Error al enviar la invitación');
        setIsInviteErrorModalOpen(true);
      }
    } catch (error) {
      console.error('Error inviting friend:', error);
      setInviteErrorMessage('Error de conexión. Por favor intenta de nuevo.');
      setIsInviteErrorModalOpen(true);
    }
  };

  // Handle card click navigation
  const handleCardClick = () => {
    if (isVideo) {
      const videoSlug = (_class as VideoInterface | undefined)?.slug;
      if (videoSlug) {
        navigate(`/clases-online/video/${videoSlug}`);
        return;
      }
    }
    if (data.class?.slug && data.class?.studio?.slug) {
      const timestamp = Math.floor(new Date(data.startTime).getTime());
      navigate(`/clases/${data.class.studio.slug}/${data.class.slug}/${timestamp}`);
    }
  };

  return (
    <div
      className="border border-gray-300 rounded-lg overflow-hidden bg-white max-w-5xl mx-auto shadow-sm hover:shadow-md transition-shadow duration-200 mb-5 cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="px-4 py-3 border-b border-gray-300">
        {/* Mobile layout: date left, time right (single line); next line has label + studio + Marcado Presente */}
        <div className="md:hidden">
          <div className="flex items-center justify-between text-xs font-sans text-gray-700">
            <div className="flex items-center space-x-1">
              <CalendarDays className="w-4 h-4" />
              <span className="whitespace-nowrap">{dateStr}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span className="whitespace-nowrap">{timeStr}</span>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2 text-xs font-sans">
            <div className="flex items-center space-x-2">
              <ClassTypeLabel
                isCustom={!!data.customUserClass}
                isVideo={!!data.video || !!data.userVideo}
                liveStreamClass={data?.class?.liveStreamClass}
              />

            </div>
            {attended && (
              <div className="flex items-center space-x-1 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium text-xs font-sans">Marcado Presente</span>
              </div>
            )}
          </div>
          <div className="text-xs text-holos-dark uppercase font-bold mt-2">{data.class?.studio?.name}</div>
        </div>

        {/* Desktop layout: original */}
        <div className="hidden md:flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-wrap">
            <ClassTypeLabel
              isCustom={!!data.customUserClass}
              isVideo={!!data.video || !!data.userVideo}
              liveStreamClass={data?.class?.liveStreamClass}
            />
            <span className="text-xs text-holos-dark uppercase font-bold">{data.class?.studio?.name}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600 flex-wrap justify-end">
            <div className="flex items-center space-x-1 ml-4">
              <CalendarDays className="w-4 h-4" />
              <span>{dateStr} | {timeStr}</span>
            </div>
            <div className="flex items-center space-x-1 ml-4">
              <Clock className="w-4 h-4" />
              <span>{durationMin} Min</span>
            </div>
            {attended && (
              <div className="flex items-center space-x-1 text-green-600 ml-4">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Marcado Presente</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 py-3">
        <div className="flex items-baseline justify-between">
          <div>
            <h3 className="text-lg font-semibold">{_class?.title}</h3>
            {/* Invite Friend Link - only for upcoming classes */}
            {
              isUpcoming &&
              !isVideo &&
              !data?.class?.liveStreamClass &&
              partnerType !== 'psychologist' &&
              partnerType !== 'nutritionist' &&
              partnerType !== 'healthCoach' &&
              partnerType !== 'financialAdvisor' &&
              partnerType !== 'fitnessCoach' &&
              partnerType !== 'corporate' &&
              ((_class as ClassInterface)?.remainingClasses || 1) > 1 && (
                <button
                  className="flex items-center mt-2 text-holos text-sm underline hover:text-holos-dark font-medium cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsInviteModalOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Invitar Amigo
                </button>
              )
            }
            {code && !data?.class?.liveStreamClass && <p className="mt-1 text-sm text-gray-600">
              Tu código secreto: <span className="font-mono">{code}</span>
            </p>}
          </div>
          <div className="flex items-center space-x-4">
            {/* Cancel button for upcoming classes */}
            {isUpcoming && (
              <div className="col-md-3 col-sm-3 col-xs-3 classes-status-wrap">
                <div className="cancel-dashboard">
                  <div
                    className="red-btn btn caps bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded cursor-pointer text-xs font-bold uppercase"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsCancelModalOpen(true);
                    }}
                  >
                    Cancelar
                  </div>
                </div>
              </div>
            )}

            {canBeReviewed && (
              <div className="col-md-3 col-sm-3 col-xs-3 classes-status-wrap">
                <button
                  className="bg-holos-dark hover:bg-holos-dark/80 text-white px-4 py-2 rounded cursor-pointer text-xs font-bold uppercase"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpenReviewModal(true);
                  }}
                >
                  <Star className="w-4 h-4 inline-block mr-1" />
                  Evaluar Clase
                </button>
              </div>
            )}
          </div>
        </div>
        {hasNotEnded && data?.class?.liveStreamClass && (
          <LiveStreamJoinButton
            userClass={data}
          />
        )}
      </div>

      {/* Cancel Class Modal */}
      <CancelClassModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        cancelClassData={getCancelClassData()}
        cancelClassConfirm={handleCancelClass}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={isSuccessModalOpen}
        onClose={handleSuccessModalClose}
        title="¡Clase Cancelada!"
        message="Tu clase ha sido cancelada satisfactoriamente. Puedes revisar tu agenda para confirmar los cambios."
        buttonText="Ver Mi Agenda"
      />

      <StudioReviewModal
        isOpen={isOpenReviewModal}
        onClose={() => window.location.reload()}
        userClass={data as any}
      />
      {/* <Modal isOpen={isOpenVideoReviewModal} onClose={() => window.location.reload()}>
        <VideoReviewModal
          video={_class as VideoInterface}
        />
      </Modal> */}

      {/* Invite Friend Modal */}
      <InviteAFriendModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        userClass={{
          _id: data._id,
          startTime: data.startTime,
          endTime: data.endTime,
          class: data.class ? {
            title: data.class.title || '',
            studio: {
              name: data.class.studio?.name || '',
              address: {
                street: data.class.studio?.address?.street || '',
                state: data.class.studio?.address?.state || data.class.studio?.address?.city || ''
              },
              district: data.class.studio?.district && typeof data.class.studio.district === 'object' ? { name: data.class.studio.district.name } : undefined
            }
          } : undefined,
          video: data.video ? {
            title: data.video.title || '',
            studio: {
              name: data.video.studio?.name || '',
              address: {
                street: data.video.studio?.address?.street || '',
                state: data.video.studio?.address?.state || data.video.studio?.address?.city || ''
              },
              district: data.video.studio?.district && typeof data.video.studio.district === 'object' ? { name: data.video.studio.district.name } : undefined
            }
          } : undefined
        }}
        user={{
          remainingClassInvites: 5, // This should come from user context
          plan: {
            numberOfPasses: passes || 0
          }
        }}
        liveStreamClass={data.class?.liveStreamClass || false}
        isVideo={isVideo}
        onInvite={handleInviteFriend}
      />

      {/* Invite Success Modal */}
      <SuccessModal
        isOpen={isInviteSuccessModalOpen}
        onClose={() => setIsInviteSuccessModalOpen(false)}
        title="¡Invitación Enviada!"
        message="Tu amigo recibirá la invitación en su correo y su clase no será agendada hasta que acepte en su cuenta."
        buttonText="Entendido"
      />

      {/* Invite Error Modal */}
      <ErrorModal
        isOpen={isInviteErrorModalOpen}
        onClose={() => {
          setIsInviteErrorModalOpen(false);
          setInviteErrorMessage('');
        }}
        title="Error al Enviar Invitación"
        message={inviteErrorMessage}
        onRetry={() => {
          setIsInviteErrorModalOpen(false);
          setIsInviteModalOpen(true);
        }}
        retryText="Intentar de Nuevo"
      />
      <ErrorModal
        isOpen={isCancelErrorModalOpen}
        onClose={() => {
          setIsCancelErrorModalOpen(false);
          setCancelErrorMessage('');
        }}
        title="Error al Cancelar Clase"
        message={cancelErrorMessage}
        onRetry={() => {
          setIsCancelErrorModalOpen(false);
          setIsCancelModalOpen(true);
        }}
        retryText="Intentar de Nuevo"
      />
    </div>
  );
};
